import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Home, Building2, Building, Filter, Search, RefreshCw, Bookmark } from "lucide-react";
import LocationAutocomplete from "./LocationAutocomplete";

const FilterPanel = ({ filters, setFilters, onSearch, loading, smartSort, setSmartSort, investmentFilters, setInvestmentFilters, multiSubTypes, setMultiSubTypes, sortBy, setSortBy, listingStatuses, setListingStatuses, onSaveSearch }) => {
  const [propertyTypes, setPropertyTypes] = useState({
    single_family: false,
    multi_family: false,
    townhouse: false
  });
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const activeFilterCount = [
    filters.min_price, filters.max_price, filters.min_beds, filters.min_baths,
    investmentFilters?.onlyPositiveCashFlow, investmentFilters?.only1PercentRule, investmentFilters?.only50PercentRule,
    investmentFilters?.minCapRate, investmentFilters?.minRent, investmentFilters?.onlyBRRRR,
    smartSort, multiSubTypes?.duplex, multiSubTypes?.triplex, multiSubTypes?.quadplex
  ].filter(Boolean).length;

  const handlePropertyTypeChange = (type) => {
    const newTypes = { ...propertyTypes, [type]: !propertyTypes[type] };
    setPropertyTypes(newTypes);
    const selectedTypes = Object.entries(newTypes)
      .filter(([_, selected]) => selected)
      .map(([type]) => type);
    setFilters({ ...filters, property_types: selectedTypes.length > 0 ? selectedTypes.join(',') : null });
  };

  return (
    <Card className="bg-card border-border sticky top-4" data-testid="filter-panel">
      <CardContent className="p-4 space-y-4">
        <div>
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">Location</Label>
          <LocationAutocomplete
            value={filters.location}
            onChange={(val) => setFilters({ ...filters, location: val })}
            onSelect={(display) => setFilters({ ...filters, location: display })}
            placeholder="City, State, or Zip Code"
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">e.g., Miami FL, 33165, Houston TX</p>
        </div>
        <Separator />
        <div>
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-3 block">Property Type</Label>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => handlePropertyTypeChange('single_family')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${propertyTypes.single_family ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-single-family">
              <Home className="w-3.5 h-3.5" /> Single Family
            </button>
            <button onClick={() => handlePropertyTypeChange('multi_family')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${propertyTypes.multi_family ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-multi-family">
              <Building2 className="w-3.5 h-3.5" /> Multi Family
            </button>
            <button onClick={() => handlePropertyTypeChange('townhouse')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${propertyTypes.townhouse ? 'bg-amber-500/15 border-amber-500/40 text-amber-500' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-townhouse">
              <Building className="w-3.5 h-3.5" /> Townhouse
            </button>
          </div>
          {propertyTypes.multi_family && multiSubTypes && (
            <div className="mt-3 pl-2 border-l-2 border-blue-500/30">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">Unit Type</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: 'duplex', label: 'Duplex', hint: '2 units' },
                  { key: 'triplex', label: 'Triplex', hint: '3 units' },
                  { key: 'quadplex', label: 'Quadplex', hint: '4+ units' },
                ].map(({ key, label, hint }) => (
                  <button key={key} onClick={() => setMultiSubTypes(prev => ({ ...prev, [key]: !prev[key] }))} className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${multiSubTypes[key] ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'bg-secondary/20 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid={`filter-${key}`} title={hint}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <Separator />
        {listingStatuses && (
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-3 block">Listing Status</Label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setListingStatuses(prev => ({ ...prev, for_sale: !prev.for_sale }))} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${listingStatuses.for_sale ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-status-for_sale">For Sale</button>
              <button onClick={() => setListingStatuses(prev => ({ ...prev, for_rent: !prev.for_rent }))} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${listingStatuses.for_rent ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-status-for_rent">For Rent</button>
              <button onClick={() => setListingStatuses(prev => ({ ...prev, recently_sold: !prev.recently_sold }))} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${listingStatuses.recently_sold ? 'bg-purple-500/15 border-purple-500/40 text-purple-500' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-status-recently_sold">Recently Sold</button>
              <button onClick={() => setListingStatuses(prev => ({ ...prev, seized: !prev.seized }))} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${listingStatuses.seized ? 'bg-orange-500/15 border-orange-500/40 text-orange-500' : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'}`} data-testid="filter-status-seized">Seized</button>
            </div>
          </div>
        )}
        <Separator />
        {/* Just Listed — investor-friendly recency filter. Radio-style: only
             one window active at a time. Click active chip to clear. */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider block">Just Listed</Label>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 font-semibold uppercase tracking-wider">NEW</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="just-listed-chips">
            {[
              { days: 1, label: "24 hours" },
              { days: 3, label: "3 days" },
              { days: 7, label: "7 days" },
              { days: 14, label: "14 days" },
            ].map(({ days, label }) => {
              const active = filters.listed_within_days === days;
              return (
                <button
                  key={days}
                  onClick={() => setFilters(f => ({ ...f, listed_within_days: active ? null : days }))}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    active
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500'
                      : 'bg-secondary/30 border-border text-muted-foreground hover:border-muted-foreground/50'
                  }`}
                  data-testid={`filter-listed-within-${days}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <Separator />
        {/* Profit Only toggle — visible by default */}
        {investmentFilters && (
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="profit-only-toggle-main">
            <div>
              <p className="text-sm font-medium text-foreground">Profit Only</p>
              <p className="text-[10px] text-muted-foreground">Show positive cash flow properties</p>
            </div>
            <button onClick={() => setInvestmentFilters(prev => ({ ...prev, onlyPositiveCashFlow: !prev.onlyPositiveCashFlow }))} data-testid="filter-onlyPositiveCashFlow" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.onlyPositiveCashFlow ? 'bg-emerald-500' : 'bg-muted'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.onlyPositiveCashFlow ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        )}
        <Separator />
        {/* Sort By — always visible */}
        <div data-testid="sort-by-section">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Sort By</Label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" data-testid="sort-by-select">
            <option value="default">Default</option>
            <optgroup label="Deal Quality">
              <option value="deal_score">Best Deal Score</option>
              <option value="deal_score_low">Lowest Deal Score</option>
              <option value="brrrr_score">Best BRRRR Potential</option>
            </optgroup>
            <optgroup label="Cash Flow">
              <option value="cash_flow">Highest Cash Flow</option>
              <option value="cash_flow_low">Lowest Cash Flow</option>
            </optgroup>
            <optgroup label="Returns">
              <option value="cap_rate">Highest Cap Rate</option>
              <option value="cap_rate_low">Lowest Cap Rate</option>
            </optgroup>
            <optgroup label="Price">
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </optgroup>
            <optgroup label="Flip Strategy">
              <option value="flip_profit">Highest Flip Profit</option>
              <option value="flip_profit_low">Lowest Flip Profit</option>
            </optgroup>
            <optgroup label="Airbnb Strategy">
              <option value="airbnb_income">Highest Airbnb Income</option>
              <option value="airbnb_income_low">Lowest Airbnb Income</option>
            </optgroup>
          </select>
        </div>
        <Separator />
        <button onClick={() => setShowMoreFilters(!showMoreFilters)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-secondary/40 hover:bg-secondary/60 transition-colors text-sm" data-testid="more-filters-toggle">
          <span className="flex items-center gap-2 text-muted-foreground">
            <Filter className="w-4 h-4" /> More Filters
            {activeFilterCount > 0 && <Badge className="bg-emerald-500 text-white border-0 text-[10px] px-1.5 py-0 h-4 min-w-4 flex items-center justify-center">{activeFilterCount}</Badge>}
          </span>
          <svg className={`w-4 h-4 text-muted-foreground transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {showMoreFilters && (
          <div className="space-y-4 pt-1 animate-fade-in">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Price</Label>
                <Input type="number" value={filters.min_price || ''} onChange={(e) => setFilters({ ...filters, min_price: e.target.value ? parseInt(e.target.value) : null })} className="mt-1 font-mono" placeholder="Any" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Max Price</Label>
                <Input type="number" value={filters.max_price || ''} onChange={(e) => setFilters({ ...filters, max_price: e.target.value ? parseInt(e.target.value) : null })} className="mt-1 font-mono" placeholder="Any" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Beds</Label>
                <Input type="number" min={0} max={10} value={filters.min_beds || ''} onChange={(e) => setFilters({ ...filters, min_beds: e.target.value ? parseInt(e.target.value) : null })} className="mt-1 font-mono" placeholder="Any" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Baths</Label>
                <Input type="number" min={0} max={10} step={0.5} value={filters.min_baths || ''} onChange={(e) => setFilters({ ...filters, min_baths: e.target.value ? parseFloat(e.target.value) : null })} className="mt-1 font-mono" placeholder="Any" />
              </div>
            </div>
            <Separator />
            {smartSort !== undefined && (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/50 border border-border">
                <div><p className="text-sm font-medium text-foreground">Smart Sort</p><p className="text-xs text-muted-foreground">Rank by your saves & alerts</p></div>
                <button onClick={() => setSmartSort(!smartSort)} data-testid="smart-sort-toggle" className={`relative w-11 h-6 rounded-full transition-colors ${smartSort ? 'bg-emerald-500' : 'bg-muted'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${smartSort ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            )}
            {investmentFilters && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Investment Filters</p>
                <div className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                  <span className="text-sm text-foreground">Passes 1% Rule</span>
                  <button onClick={() => setInvestmentFilters(prev => ({ ...prev, only1PercentRule: !prev.only1PercentRule }))} data-testid="filter-only1PercentRule" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.only1PercentRule ? 'bg-blue-500' : 'bg-muted'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.only1PercentRule ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                  <span className="text-sm text-foreground">Passes 50% Rule</span>
                  <button onClick={() => setInvestmentFilters(prev => ({ ...prev, only50PercentRule: !prev.only50PercentRule }))} data-testid="filter-only50PercentRule" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.only50PercentRule ? 'bg-amber-500' : 'bg-muted'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.only50PercentRule ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Cap Rate %</Label>
                    <Input type="number" min={0} max={30} step={0.5} value={investmentFilters.minCapRate || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minCapRate: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 6" data-testid="filter-minCapRate" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Rent $/mo</Label>
                    <Input type="number" min={0} step={100} value={investmentFilters.minRent || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minRent: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 2000" data-testid="filter-minRent" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Cash Flow $/mo</Label>
                    <Input type="number" min={0} step={50} value={investmentFilters.minCashFlow || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minCashFlow: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 500" data-testid="filter-minCashFlow" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Deal Score</Label>
                    <Input type="number" min={0} max={100} step={5} value={investmentFilters.minDealScore || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minDealScore: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 70" data-testid="filter-minDealScore" />
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                    <span className="text-sm text-foreground font-medium">BRRRR Potential</span>
                    <button onClick={() => setInvestmentFilters(prev => ({ ...prev, onlyBRRRR: !prev.onlyBRRRR }))} data-testid="filter-onlyBRRRR" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.onlyBRRRR ? 'bg-violet-500' : 'bg-muted'}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.onlyBRRRR ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed px-1">
                    Shows properties with BRRRR score 60+. Score is based on: equity spread vs Zestimate (30%), rent-to-price ratio (25%), cap rate (20%), days on market (15%), and below-market pricing (10%).
                  </p>
                </div>
                <Separator />
                <p className="text-xs text-orange-500 uppercase tracking-wider font-semibold">Flipper Buy Box</p>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">MAO Rule</Label>
                    <select value={investmentFilters.maoRule || '75'} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, maoRule: e.target.value }))} className="mt-1 w-full rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" data-testid="filter-maoRule">
                      <option value="70">70% Rule (Conservative)</option>
                      <option value="75">75% Rule (Standard)</option>
                      <option value="80">80% Rule (Aggressive)</option>
                      <option value="custom">Custom %</option>
                    </select>
                    {investmentFilters.maoRule === 'custom' && (
                      <Input type="number" min={50} max={95} step={1} value={investmentFilters.maoCustomPct || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, maoCustomPct: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 72" data-testid="filter-maoCustomPct" />
                    )}
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg bg-orange-500/5 border border-orange-500/20">
                    <span className="text-sm text-foreground">Passes MAO Rule</span>
                    <button onClick={() => setInvestmentFilters(prev => ({ ...prev, onlyPassesMAO: !prev.onlyPassesMAO }))} data-testid="filter-onlyPassesMAO" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.onlyPassesMAO ? 'bg-orange-500' : 'bg-muted'}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.onlyPassesMAO ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Flip Profit $</Label>
                      <Input type="number" min={0} step={5000} value={investmentFilters.minFlipProfit || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minFlipProfit: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 40000" data-testid="filter-minFlipProfit" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min Flip ROI %</Label>
                      <Input type="number" min={0} max={200} step={5} value={investmentFilters.minFlipROI || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minFlipROI: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="e.g. 20" data-testid="filter-minFlipROI" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider">Min ARV $</Label>
                      <Input type="number" min={0} step={25000} value={investmentFilters.minARV || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, minARV: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="Any" data-testid="filter-minARV" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider">Max ARV $</Label>
                      <Input type="number" min={0} step={25000} value={investmentFilters.maxARV || ''} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, maxARV: e.target.value ? parseFloat(e.target.value) : null }))} className="mt-1 font-mono" placeholder="Any" data-testid="filter-maxARV" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Max Rehab Level</Label>
                    <select value={investmentFilters.maxRehabLevel || 'any'} onChange={(e) => setInvestmentFilters(prev => ({ ...prev, maxRehabLevel: e.target.value }))} className="mt-1 w-full rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" data-testid="filter-maxRehabLevel">
                      <option value="any">Any</option>
                      <option value="cosmetic">Cosmetic Only</option>
                      <option value="moderate">Moderate or Less</option>
                      <option value="heavy">Heavy or Less</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                    <span className="text-sm text-foreground">No Flood Zone</span>
                    <button onClick={() => setInvestmentFilters(prev => ({ ...prev, noFloodZone: !prev.noFloodZone }))} data-testid="filter-noFloodZone" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.noFloodZone ? 'bg-blue-500' : 'bg-muted'}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.noFloodZone ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                </div>
                <Separator />
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Strategy Views</p>
                <div className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                  <div>
                    <span className="text-sm text-foreground font-medium">Show Flip Data</span>
                    <p className="text-[10px] text-muted-foreground">ARV, rehab cost, flip profit on cards</p>
                  </div>
                  <button onClick={() => setInvestmentFilters(prev => ({ ...prev, showFlipData: !prev.showFlipData }))} data-testid="filter-showFlipData" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.showFlipData ? 'bg-pink-500' : 'bg-muted'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.showFlipData ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                  <div>
                    <span className="text-sm text-foreground font-medium">Show Airbnb Data</span>
                    <p className="text-[10px] text-muted-foreground">Nightly rate, occupancy, STR income on cards</p>
                  </div>
                  <button onClick={() => setInvestmentFilters(prev => ({ ...prev, showAirbnbData: !prev.showAirbnbData }))} data-testid="filter-showAirbnbData" className={`relative w-10 h-5 rounded-full transition-colors ${investmentFilters.showAirbnbData ? 'bg-cyan-500' : 'bg-muted'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${investmentFilters.showAirbnbData ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              </div>
            )}
            <Separator />
          </div>
        )}
        <Button className="w-full" onClick={onSearch} disabled={loading} data-testid="search-btn">
          {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          Search Properties
        </Button>
        {onSaveSearch && (
          <Button variant="outline" className="w-full" onClick={onSaveSearch} data-testid="save-search-btn">
            <Bookmark className="w-4 h-4 mr-2" /> Save This Search
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default FilterPanel;
